import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

const HERO =
  "https://images.unsplash.com/photo-1777652918682-dec5c79d1301?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNhcmQlMjBleGVjdXRpdmUlMjBuZXR3b3JraW5nfGVufDB8fHx8MTc5MDA5OTE5Mnww&ixlib=rb-4.1.0&q=85";

function AuthShell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0F17]">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="Networking" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <img src="/dextyou-logo.png" alt="Dextyou" className="w-56 rounded-2xl bg-white p-3 mb-6 shadow-2xl" />
          <h2 className="font-heading text-4xl font-extrabold tracking-tight leading-tight max-w-md">
            Snap a card. <span className="text-[#2EA6FF]">Skip the typing.</span>
          </h2>
          <p className="text-slate-400 mt-4 max-w-md text-base leading-relaxed">
            Your business cards, scanned by AI and organized like a modern Rolodex — searchable,
            shareable, always in your pocket.
          </p>
          <p className="font-heading text-xl font-bold text-[#2EA6FF] mt-6">Let Me Dextyou</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0A2540] to-[#2EA6FF] flex items-center justify-center">
              <span className="font-heading text-white font-extrabold text-lg leading-none">D</span>
            </div>
            <span className="font-heading text-xl font-extrabold tracking-tight">Dextyou</span>
          </div>
          <p className="overline text-blue-500 mb-2">{subtitle}</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-8">{title}</h1>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

const inputClass =
  "w-full bg-[#1E293B] border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Let Me Dextyou">
      <form onSubmit={submit} className="space-y-4">
        {error && (
          <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/60 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Password</label>
          <div className="relative">
            <input
              data-testid="login-password-input"
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-xs text-blue-500 hover:text-blue-400">
            Forgot password?
          </Link>
        </div>
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl py-3 flex items-center justify-center transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </button>
      </form>
      <p className="text-sm text-slate-400 mt-6 text-center">
        No account?{" "}
        <Link to="/register" className="text-blue-500 hover:text-blue-400 font-medium">
          Create one
        </Link>
      </p>
    </AuthShell>
  );
}

export { AuthShell, inputClass };
